// Plan gate for Shopify-installed workspaces (2026-09-26, user: "disable the menu and top bar
// until the plan is selected"). A store that installed Plunk Commerce from Shopify has no
// Commerce plan until the merchant picks one on Shopify's pricing page; until then the App
// shows ONLY the plan step — no sidebar, no top-bar controls, no deep links.
//   gated  = Shopify workspace AND no live `commerce` plan (Billing /v1/billing/status.plans)
//   unknown (no billing answer, not a Shopify workspace) = never gated — fail open.
import account from "../data/account";
import endpoints from "../endpoints";
import { AuthBase } from "../../plunk/service/auth-base";
import { getLivePlans, getBillingSources, getBillingStatus } from "./billing-status";
const KEY = () => `plunk-shopify-shop:${(account.get() || {}).tenantId || "ws"}`;
const TTL = 60 * 1000;
let _cache = null; // { at, gated, shop }

export function rememberShop(shop) {
  if (!shop) return;
  try {
    localStorage.setItem(KEY(), String(shop).trim().toLowerCase());
  } catch (e) {/* private mode */}
}
export function getShop() {
  try {
    return localStorage.getItem(KEY()) || "";
  } catch (e) {
    return "";
  }
}
// Every Shopify landing carries ?shopify=<phase>&shop=<domain> (/Shopify plan/provisioning steps
// AND the post-install /Setup?shopify=connected&shop=…) — remember the store from any of them.
export function rememberShopFromUrl() {
  try {
    const q = new URLSearchParams(window.location.search || "");
    const s = (q.get("shop") || "").trim();
    if (q.get("shopify") && s) rememberShop(s);
  } catch (e) {/* noop */}
}
export function clearPlanGateCache() {
  _cache = null;
}
export function forgetShop() {
  try {
    localStorage.removeItem(KEY());
  } catch (e) {/* private mode */}
}

// A remembered store counts only when it really belongs to this workspace (2026-10-22): a browser signed into another
// workspace that passed through a store's install must not lock that workspace. Unknown answer → trust the memory.
const _mine = {};
async function storeIsMine(shop) {
  const tenant = ((account.get() || {}).tenantId || "").trim();
  const k = tenant + "|" + shop;
  if (k in _mine) return _mine[k];
  try {
    const host = endpoints && endpoints.product && endpoints.product.host || "api.plunk.io";
    const res = await fetch(`https://${host}/shopify/connected?shop=${encodeURIComponent(shop)}`, {
      headers: {
        "Authorization": "Bearer " + AuthBase.idToken,
        "x-app-id": tenant
      }
    });
    if (!res.ok) return true;
    const d = await res.json();
    _mine[k] = !!(d && d.mine);
    return _mine[k];
  } catch (e) {
    return true;
  }
}
export async function getPlanGate(force) {
  if (!force && _cache && Date.now() - _cache.at < TTL) return _cache;
  // Only a browser that landed here from a Shopify install (/Shopify?shopify=…&shop=) can be
  // gated — a Plunk-first workspace that merely connected a Shopify feed is never locked.
  rememberShopFromUrl();
  let shop = getShop();
  let gated = false;
  const [plans, sources, status] = await Promise.all([getLivePlans(force), getBillingSources(force), getBillingStatus(force)]);
  // Plunk Commerce was uninstalled (Billing ended its plan, 2026-10-22): locked on every browser, and the plan step
  // offers only "Reinstall".
  if (status && status.shopifyUninstalled) {
    if (status.shopifyShop) {
      shop = String(status.shopifyShop).toLowerCase();
      rememberShop(shop);
    }
    _cache = {
      at: Date.now(),
      gated: true,
      uninstalled: true,
      shop
    };
    return _cache;
  }
  // null → billing unreachable → fail open; any non-Shopify live plan (Stripe/managed) → not gated
  if (Array.isArray(plans) && Array.isArray(sources)) {
    const billedElsewhere = sources.some(s => s && s !== "shopify");
    // Shopify-billed workspace = a remembered install landing in this browser, OR at least one live
    // plan and every live plan billed by Shopify (e.g. Studio plan present, Commerce not yet picked).
    let remembered = !!shop;
    if (remembered && !(sources.length > 0 && sources.every(s => s === "shopify")) && !(await storeIsMine(shop))) {
      forgetShop();
      shop = "";
      remembered = false;
    }
    const shopifyBilled = remembered || sources.length > 0 && sources.every(s => s === "shopify");
    gated = shopifyBilled && !billedElsewhere && plans.indexOf("commerce") === -1;
  }
  _cache = {
    at: Date.now(),
    gated,
    uninstalled: false,
    shop
  };
  return _cache;
}

// Route the gate applies to: everything except the plan step itself and sign-in/out.
export function planStepUrl(shop) {
  return `/Shopify?shopify=plan${shop ? "&shop=" + encodeURIComponent(shop) : ""}`;
}
export function isExemptPath(pathname) {
  const p = String(pathname || "").toLowerCase();
  return p === "/shopify" || p.indexOf("/auth/") === 0;
}